import React, { useState, useEffect, useRef } from "react";
import Select from "react-select";
import countryList from "react-select-country-list";
import { Controller } from "react-hook-form";

// React Router Dom
import { useNavigate, useSearchParams } from "react-router-dom";

// React Icons
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { FaRegCalendarAlt } from "react-icons/fa";

// Context
import { useAuth } from "../contexts/AuthContext";

// React Hook Form
import { useForm } from "react-hook-form";

// Yup
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";

// Country codes list (user will supply the actual data)
import { COUNTRY_CODES } from "../utils/countryCode";

// Hooks
import useSnackbarNotification from "../hooks/useSnackbarNotification";

const schema = yup.object().shape({
    firstName: yup.string().required("First name is required"),
    lastName: yup.string().required("Last name is required"),
    email: yup
        .string()
        .email("Please enter a valid email address")
        .matches(
            /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
            "Please enter a valid email address"
        )
        .required("Email is required"),
    countryCode: yup.string().required("Country code is required"),
    cellNumber: yup
        .string()
        .matches(
            /^[0-9]{6,15}$/,
            {
                message:
                    "Please enter a valid phone number (numbers only, 6-15 digits)",
                excludeEmptyString: true,
            }
        )
        .transform((value) => (value ? value.replace(/\s+/g, "") : value))
        .nullable()
        .optional(),
    dateOfBirth: yup
        .string()
        .required("Date of birth is required")
        .test("valid-format", "Please enter a valid date (YYYY-MM-DD)", (value) => {
            if (!value) return false;
            const normalized = value.trim().replace(/\./g, "/").replace(/\//g, "-");
            if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) return false;
            const date = new Date(normalized + "T00:00:00");
            return !isNaN(date.getTime());
        })
        .test("not-in-future", "Date of birth cannot be in the future", (value) => {
            if (!value) return false;
            const normalized = value.trim().replace(/\./g, "/").replace(/\//g, "-");
            const date = new Date(normalized + "T00:00:00");
            const today = new Date();
            today.setHours(0,0,0,0);
            return date <= today;
        })
        .test("min-date", "Date of birth must be after 1900-01-01", (value) => {
            if (!value) return false;
            const normalized = value.trim().replace(/\./g, "/").replace(/\//g, "-");
            const date = new Date(normalized + "T00:00:00");
            const min = new Date(1900, 0, 1);
            return date >= min;
        }),
    sex: yup.string().required("Sex is required"),
    trainingLevel: yup.string().required("Training level is required"),
    employmentStatus: yup.string().required("Employment status is required"),
    howDidYouFindUs: yup.string().required("How did you find us is required"),
    howDidYouFindUsOther: yup.string().when('howDidYouFindUs', {
        is: 'other',
        then: (schema) => schema.required('Please specify how you found us'),
        otherwise: (schema) => schema.optional()
    }),
    password: yup
        .string()
        .min(6, "Password must be at least 6 characters")
        .required("Password is required"),
    confirmPassword: yup
        .string()
        .oneOf([yup.ref("password"), null], "Passwords must match")
        .required("Confirm password is required"),
    agreeToTerms: yup.boolean().oneOf([true], "You must agree to the terms"),
    confirmMedicalField: yup
        .boolean()
        .oneOf([true], "You must confirm you are in the medical field"),
    country: yup.string().required("Country is required"),
    receiveJobAlerts: yup.boolean(),
    isFirstTimeUser: yup.boolean().nullable().optional(),
});

const SignUpPage = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const redirectParam = searchParams.get('redirect');
    const { showError, showSuccess } = useSnackbarNotification();
    const { signUp } = useAuth();

    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [showOtherInput, setShowOtherInput] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const hiddenDateInputRef = useRef(null);

    const handleDobTextChange = (e) => {
        const raw = e.target.value.replace(/[^0-9]/g, "");
        const year = raw.slice(0, 4);
        const month = raw.slice(4, 6);
        const day = raw.slice(6, 8);
        let formatted = year;
        if (month.length) {
            formatted += `-${month}`;
        }
        if (day.length) {
            formatted += `-${day}`;
        }
        e.target.value = formatted;
        setValue('dateOfBirth', formatted, { shouldValidate: true, shouldDirty: true });
    };

    useEffect(() => {
        const mq = window.matchMedia && window.matchMedia("(max-width: 767px)");
        const update = () => setIsMobile(mq ? mq.matches : window.innerWidth <= 767);
        update();
        if (mq && mq.addEventListener) {
            mq.addEventListener("change", update);
            return () => mq.removeEventListener("change", update);
        }
        return () => {};
    }, []);

    const {
        register,
        handleSubmit,
        formState: { errors },
        control,
        setValue,
    } = useForm({
        resolver: yupResolver(schema),
        mode: "onChange",
        defaultValues: {
            countryCode: "+27", // default; user may change
        },
    });

    const onSubmit = async (data) => {
        setLoading(true);
        setSuccessMessage("");

        try {
            // Combine country code with cell number if provided
            const fullPhoneNumber = data.cellNumber
                ? `${data.countryCode}${data.cellNumber}`
                : undefined;

            // Determine the final value for how did you find us
            const howDidYouFindUsValue = showOtherInput 
                ? data.howDidYouFindUsOther 
                : data.howDidYouFindUs;

            const normalizedDob = data.dateOfBirth
                ? data.dateOfBirth.trim().replace(/\./g, "/").replace(/\//g, "-")
                : undefined;
                //store email in localStorage for use in confirm email page
            localStorage.setItem('signupEmail', data.email);
            const { error, data: userData } = await signUp(
                data.email,
                data.password,
                {
                    first_name: data.firstName,
                    last_name: data.lastName,
                    cell_number: fullPhoneNumber,
                    date_of_birth: normalizedDob,
                    sex: data.sex,
                    training_level: data.trainingLevel,
                    employment_status: data.employmentStatus,
                    how_did_you_find_us: howDidYouFindUsValue,
                    receive_job_alerts: data.receiveJobAlerts,
                    country: data.country,
                    isFirstTimeUser: true
                }
            );

            if (error) {
                // Check if the error is due to existing email
                if (error.message?.toLowerCase().includes('user already registered') || 
                    error.message?.toLowerCase().includes('already registered') ||
                    error.code === 'signup_disabled' ||
                    (userData?.user && userData.user.identities && userData.user.identities.length === 0)) {
                    showError("An account with this email already exists. Please sign in instead.");
                    setLoading(false);
                    return;
                }
                throw error;
            }

            // Check if user already exists (no identities returned)
            if (userData?.user?.identities?.length === 0) {
                showError("An account with this email already exists. Please sign in instead.");
                setLoading(false);
                return;
            }

            showSuccess(
                "Your account has been created! Please check your email for verification instructions. If you don't see the email, check your spam/junk folder."
            );
            setSuccessMessage(
                "Your account has been created! Please check your email for verification instructions. If you don't see the email, check your spam/junk folder."
            );
            setLoading(false);
            
            // Store redirect intent in localStorage if redirect parameter is set
            if (redirectParam === 'mock-interview') {
                localStorage.setItem('redirectAfterLogin', 'mock-interview');
            }
            
            setTimeout(() => {
                // const redirectPath = redirectParam === 'mock-interview' 
                //     ? '/login?redirect=mock-interview' 
                //     : '/login';
                // navigate(redirectPath);
                navigate('/confirm-email');
            }, 5000);
        } catch (err) {
            console.error("Signup error:", err);
            // Check for specific email already exists errors
            if (err.message?.toLowerCase().includes('already registered') || 
                err.message?.toLowerCase().includes('user already registered')) {
                showError("An account with this email already exists. Please sign in instead.");
            } else {
                showError(err.message || "An error occurred while creating your account");
            }
            setLoading(false);
        }
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const toggleConfirmPasswordVisibility = () => {
        setShowConfirmPassword(!showConfirmPassword);
    };

    return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
            <div className="sm:mx-auto sm:w-full sm:max-w-2xl">
                <div className="bg-white py-8 px-10 shadow sm:rounded-lg">
                    
                    <div
                        className="logo cursor-pointer justify-center mb-8"
                        onClick={() => navigate("/")}
                    >
                        Doctors' Jobs <span className="logo-badge">Beta</span>
                    </div>

                    <h2 className="text-center text-2xl font-bold text-gray-900 mb-8">
                        Create an account
                    </h2>

                    {successMessage && (
                        <div className="mb-4 bg-green-50 border-l-4 border-green-500 p-4">
                            <p className="text-sm text-green-700">
                                {successMessage}
                            </p>
                        </div>
                    )}

                    <form
                        className="space-y-6"
                        onSubmit={handleSubmit(onSubmit)}
                    >
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* First Name */}
                            <div>
                                <label
                                    htmlFor="firstName"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    First Name *
                                </label>
                                <input
                                    {...register("firstName")}
                                    id="firstName"
                                    type="text"
                                    className={`appearance-none block w-full px-3 py-2 border ${
                                        errors.firstName
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                />
                                {errors.firstName && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.firstName.message}
                                    </p>
                                )}
                            </div>

                            {/* Last Name */}
                            <div>
                                <label
                                    htmlFor="lastName"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    Surname *
                                </label>
                                <input
                                    {...register("lastName")}
                                    id="lastName"
                                    type="text"
                                    className={`appearance-none block w-full px-3 py-2 border ${
                                        errors.lastName
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                />
                                {errors.lastName && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.lastName.message}
                                    </p>
                                )}
                            </div>

                            {/* Email */}
                            <div>
                                <label
                                    htmlFor="email"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    Email Address *
                                </label>
                                <input
                                    {...register("email")}
                                    id="email"
                                    type="email"
                                    className={`appearance-none block w-full px-3 py-2 border ${
                                        errors.email
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                />
                                {errors.email && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.email.message}
                                    </p>
                                )}
                            </div>

                            {/* Country */}
                            <div>
                                <label
                                    htmlFor="country"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    Country *
                                </label>

                                <Controller
                                    name="country"
                                    control={control}
                                    rules={{ required: "Country is required" }}
                                    render={({ field }) => (
                                        <Select
                                            {...field}
                                            inputId="country"
                                            options={[{ value: 'ZA', label: 'South Africa' }, ...countryList().getData().filter((c) => c.value !== 'ZA')]}
                                            classNamePrefix="react-select"
                                            placeholder="Select a country..."
                                            onChange={(val) => field.onChange(val?.value)}
                                            value={
                                                countryList().getData().find((c) => c.value === field.value) || null
                                            }
                                        />
                                    )}
                                />

                                {errors.country && (
                                    <p className="mt-1 text-sm text-red-600">{errors.country.message}</p>
                                )}
                            </div>

                            {/* Cell Number */}
                            <div>
                                <label
                                    htmlFor="cellNumber"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    Cell Number (optional)
                                </label>
                                <div className="flex">
                                    <select
                                        {...register("countryCode")}
                                        className="appearance-none block w-24 px-2 py-2 border border-gray-300 rounded-l-md shadow-sm bg-gray-50 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                    >
                                        {COUNTRY_CODES.map((country) => (
                                            <option
                                                key={`${country.code}-${country.country}`}
                                                value={country.code}
                                            >
                                                {country.flag} {country.code}
                                            </option>
                                        ))}
                                    </select>
                                    <input
                                        {...register("cellNumber")}
                                        id="cellNumber"
                                        type="tel"
                                        placeholder="1234567890"
                                        className={`appearance-none block w-full px-3 py-2 border border-l-0 ${
                                            errors.cellNumber
                                                ? "border-red-300"
                                                : "border-gray-300"
                                        } rounded-r-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                    />
                                </div>
                                {errors.cellNumber && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.cellNumber.message}
                                    </p>
                                )}
                            </div>

                            {/* Date of Birth */}
                            <div>
                                <label
                                    htmlFor="dateOfBirth"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    Date of Birth *
                                </label>
                                {isMobile ? (
                                    <div className="relative">
                                        <input
                                            {...register("dateOfBirth")}
                                            id="dateOfBirth"
                                            type="text"
                                            placeholder="YYYY-MM-DD"
                                            inputMode="numeric"
                                            pattern="\\d{4}[-/]\\d{2}[-/]\\d{2}"
                                            onChange={handleDobTextChange}
                                            className={`appearance-none block w-full pl-3 pr-10 py-2 border ${
                                                errors.dateOfBirth
                                                    ? "border-red-300"
                                                    : "border-gray-300"
                                            } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                        />
                                        <button
                                            type="button"
                                            aria-label="Open calendar"
                                            onClick={() => {
                                                if (hiddenDateInputRef.current) {
                                                    if (typeof hiddenDateInputRef.current.showPicker === 'function') {
                                                        hiddenDateInputRef.current.showPicker();
                                                    } else {
                                                        hiddenDateInputRef.current.click();
                                                    }
                                                }
                                            }}
                                            className="absolute right-0 top-1/2 -translate-y-1/2 -mt-2 transform px-3 flex items-center text-gray-500"
                                        >
                                            <FaRegCalendarAlt className="h-5 w-5" />
                                        </button>
                                        <input
                                            ref={hiddenDateInputRef}
                                            type="date"
                                            max={new Date().toISOString().split("T")[0]}
                                            className="hidden"
                                            onChange={(e) => {
                                                const val = e.target.value;
                                                const textEl = document.getElementById('dateOfBirth');
                                                if (textEl) {
                                                    textEl.value = val;
                                                }
                                                setValue('dateOfBirth', val, { shouldValidate: true, shouldDirty: true });
                                            }}
                                        />
                                        <p className="mt-1 text-xs text-gray-500">Example: 1990-07-15</p>
                                    </div>
                                ) : (
                                    <input
                                        {...register("dateOfBirth")}
                                        id="dateOfBirth"
                                        type="date"
                                        max={new Date().toISOString().split("T")[0]}
                                        className={`appearance-none block w-full px-3 py-2 border ${
                                            errors.dateOfBirth
                                                ? "border-red-300"
                                                : "border-gray-300"
                                        } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                    />
                                )}
                                {errors.dateOfBirth && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.dateOfBirth.message}
                                    </p>
                                )}
                            </div>

                            {/* Sex */}
                            <div>
                                <label
                                    htmlFor="sex"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    Sex *
                                </label>
                                <select
                                    {...register("sex")}
                                    id="sex"
                                    className={`appearance-none block w-full px-3 py-2 border ${
                                        errors.sex
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                >
                                    <option value="">Select</option>
                                    <option value="male">Male</option>
                                    <option value="female">Female</option>
                                    <option value="intersex">Intersex</option>
                                    <option value="other">Other</option>
                                    <option value="prefer-not-to-say">
                                        Prefer not to say
                                    </option>
                                </select>
                                {errors.sex && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.sex.message}
                                    </p>
                                )}
                            </div>

                            {/* Training Level */}
                            <div>
                                <label
                                    htmlFor="trainingLevel"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    Level of training/expertise *
                                </label>
                                <select
                                    {...register("trainingLevel")}
                                    id="trainingLevel"
                                    className={`appearance-none block w-full px-3 py-2 border ${
                                        errors.trainingLevel
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                >
                                    <option value="">Select</option>
                                    <option value="student">
                                        Medical Student
                                    </option>
                                    <option value="resident">Registrar</option>
                                    <option value="fellow">Fellow</option>
                                    <option value="attending">
                                        Consultant
                                    </option>
                                    <option value="medical-officer">
                                        Medical Officer
                                    </option>
                                    <option value="general-practitioner">
                                        General Practitioner
                                    </option>
                                    <option value="nurse">Nurse</option>
                                    <option value="PA">
                                        Physician Assistant
                                    </option>
                                    <option value="other">
                                        Other Healthcare Professional
                                    </option>
                                </select>
                                {errors.trainingLevel && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.trainingLevel.message}
                                    </p>
                                )}
                            </div>

                            {/* Employment Status */}
                            <div>
                                <label
                                    htmlFor="employmentStatus"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    Employment Status *
                                </label>
                                <select
                                    {...register("employmentStatus")}
                                    id="employmentStatus"
                                    className={`appearance-none block w-full px-3 py-2 border ${
                                        errors.employmentStatus
                                            ? "border-red-300"
                                            : "border-gray-300"
                                    } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                >
                                    <option value="">Select</option>
                                    <option value="full-time">Full-time</option>
                                    <option value="part-time">Part-time</option>
                                    <option value="in-training">
                                        In Training
                                    </option>
                                    <option value="unemployed">
                                        Unemployed
                                    </option>
                                    <option value="retired">Retired</option>
                                </select>
                                {errors.employmentStatus && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.employmentStatus.message}
                                    </p>
                                )}
                            </div>

                            {/* How did you find us */}
                            <div>
                                <label
                                    htmlFor="howDidYouFindUs"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    How did you find us *
                                </label>
                                {!showOtherInput ? (
                                    <div className="relative">
                                        <select
                                            {...register("howDidYouFindUs")}
                                            id="howDidYouFindUs"
                                            onChange={(e) => {
                                                if (e.target.value === 'other') {
                                                    setShowOtherInput(true);
                                                }
                                            }}
                                            className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                                                errors.howDidYouFindUs
                                                    ? "border-red-300"
                                                    : "border-gray-300"
                                            } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                        >
                                            <option value="">Select</option>
                                            <option value="google">Google</option>
                                            <option value="email">Email</option>
                                            <option value="friend-or-colleague">Friend or colleague</option>
                                            <option value="youtube">Youtube</option>
                                            <option value="linkedin">LinkedIn</option>
                                            <option value="instagram">Instagram</option>
                                            <option value="university-institution">University / Institution</option>
                                            <option value="other">Other</option>
                                        </select>
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="relative">
                                        <input
                                            {...register("howDidYouFindUsOther")}
                                            id="howDidYouFindUsOther"
                                            type="text"
                                            placeholder="Please specify how you found us"
                                            className={`appearance-none block w-full px-3 py-2 pr-10 border ${
                                                errors.howDidYouFindUsOther
                                                    ? "border-red-300"
                                                    : "border-gray-300"
                                            } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowOtherInput(false);
                                            }}
                                            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                                        >
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                        </button>
                                    </div>
                                )}
                                {errors.howDidYouFindUs && !showOtherInput && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.howDidYouFindUs.message}
                                    </p>
                                )}
                                {errors.howDidYouFindUsOther && showOtherInput && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.howDidYouFindUsOther.message}
                                    </p>
                                )}
                            </div>

                            {/* Password */}
                            <div>
                                <label
                                    htmlFor="password"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    Password *
                                </label>
                                <div className="relative">
                                    <input
                                        {...register("password")}
                                        id="password"
                                        type={
                                            showPassword ? "text" : "password"
                                        }
                                        className={`appearance-none block w-full px-3 py-2 border ${
                                            errors.password
                                                ? "border-red-300"
                                                : "border-gray-300"
                                        } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                    />
                                    <button
                                        type="button"
                                        onClick={togglePasswordVisibility}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showPassword ? (
                                            <FaEyeSlash className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <FaEye className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                                {errors.password && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.password.message}
                                    </p>
                                )}
                            </div>

                            {/* Confirm Password */}
                            <div>
                                <label
                                    htmlFor="confirmPassword"
                                    className="block text-base md:text-lg font-medium text-gray-700 mb-1"
                                >
                                    Confirm Password *
                                </label>
                                <div className="relative">
                                    <input
                                        {...register("confirmPassword")}
                                        id="confirmPassword"
                                        type={
                                            showConfirmPassword
                                                ? "text"
                                                : "password"
                                        }
                                        className={`appearance-none block w-full px-3 py-2 border ${
                                            errors.confirmPassword
                                                ? "border-red-300"
                                                : "border-gray-300"
                                        } rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm`}
                                    />
                                    <button
                                        type="button"
                                        onClick={
                                            toggleConfirmPasswordVisibility
                                        }
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {showConfirmPassword ? (
                                            <FaEyeSlash className="h-5 w-5 text-gray-400" />
                                        ) : (
                                            <FaEye className="h-5 w-5 text-gray-400" />
                                        )}
                                    </button>
                                </div>
                                {errors.confirmPassword && (
                                    <p className="mt-1 text-sm text-red-600">
                                        {errors.confirmPassword.message}
                                    </p>
                                )}
                            </div>
                        </div>

                        {/* Checkboxes */}
                        <div className="space-y-4 mt-6">
                            <div className="flex items-start">
                                <div className="flex items-center h-5">
                                    <input
                                        {...register("agreeToTerms")}
                                        id="agreeToTerms"
                                        type="checkbox"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label
                                        htmlFor="agreeToTerms"
                                        className="font-medium text-gray-700"
                                    >
                                        I understand by creating an account, I
                                        agree to Four Minute Medicine's{" "}
                                        <a
                                            href="https://www.fourminutemedicine.com/files/terms-and-conditions.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                        >
                                            Terms and Conditions
                                        </a>{" "}
                                        and{" "}
                                        <a
                                            href="https://www.fourminutemedicine.com/files/privacy-policy.pdf"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-blue-600 hover:underline"
                                        >
                                            privacy policy
                                        </a>
                                        . *
                                    </label>
                                </div>
                            </div>
                            {errors.agreeToTerms && (
                                <p className="mt-1 text-sm text-red-600">
                                    {errors.agreeToTerms.message}
                                </p>
                            )}

                            <div className="flex items-start">
                                <div className="flex items-center h-5">
                                    <input
                                        {...register("confirmMedicalField")}
                                        id="confirmMedicalField"
                                        type="checkbox"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label
                                        htmlFor="confirmMedicalField"
                                        className="font-medium text-gray-700"
                                    >
                                        I confirm that I am within the medical
                                        field, either as a medical student at a
                                        registered medical school or as a
                                        registered medical practitioner. *
                                    </label>
                                </div>
                            </div>
                            {errors.confirmMedicalField && (
                                <p className="mt-1 text-sm text-red-600">
                                    {errors.confirmMedicalField.message}
                                </p>
                            )}

                            <div className="flex items-start">
                                <div className="flex items-center h-5">
                                    <input
                                        {...register("receiveJobAlerts")}
                                        id="receiveJobAlerts"
                                        type="checkbox"
                                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                    />
                                </div>
                                <div className="ml-3 text-sm">
                                    <label
                                        htmlFor="receiveJobAlerts"
                                        className="font-medium text-gray-700"
                                    >
                                        Keep me up to date with new job
                                        opportunities
                                    </label>
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-center mt-8">
                            <button
                                type="submit"
                                disabled={loading}
                                className="w-full md:w-1/3 flex justify-center py-2 px-4 border border-transparent rounded-full shadow-sm text-sm font-medium text-white bg-black hover:bg-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                            >
                                {loading ? "Creating account..." : "Sign up"}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default SignUpPage;
